/**
 * SEO component: meta dasar + Open Graph/Twitter + canonical + JSON-LD
 * organisasi (Dentist) untuk setiap halaman. Structured data mendukung
 * SEO, GEO (local/entity), dan AEO (answer engines).
 */

import { graphql, useStaticQuery } from 'gatsby';

import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import React from 'react';
import { SITE, organizationSchema } from '../constants/site';

function SEO({ description, lang, meta, title, image, pathname }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  );

  const metaDescription = description || site.siteMetadata.description;
  const defaultTitle = site.siteMetadata?.title;
  const siteUrl = site.siteMetadata?.siteUrl || SITE.url;
  // Canonical/og:url hanya bila pathname diberikan, agar tidak salah menunjuk
  // semua halaman ke root.
  const canonical = pathname ? `${siteUrl}${pathname}` : null;
  const ogImage = `${siteUrl}${image || SITE.ogImage}`;

  const metaTags = [
    { name: `description`, content: metaDescription },
    // Open Graph
    { property: `og:title`, content: title },
    { property: `og:description`, content: metaDescription },
    { property: `og:type`, content: `website` },
    { property: `og:site_name`, content: defaultTitle },
    { property: `og:locale`, content: `id_ID` },
    { property: `og:image`, content: ogImage },
    // Twitter
    { name: `twitter:card`, content: `summary_large_image` },
    { name: `twitter:creator`, content: site.siteMetadata?.author || `` },
    { name: `twitter:title`, content: title },
    { name: `twitter:description`, content: metaDescription },
    { name: `twitter:image`, content: ogImage },
  ];
  if (canonical) metaTags.push({ property: `og:url`, content: canonical });

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={defaultTitle ? `%s | ${defaultTitle}` : null}
      link={canonical ? [{ rel: 'canonical', href: canonical }] : []}
      meta={metaTags.concat(meta)}>
      <script type='application/ld+json'>
        {JSON.stringify(organizationSchema())}
      </script>
    </Helmet>
  );
}

SEO.defaultProps = {
  lang: `id`,
  meta: [],
  description: ``,
  image: ``,
  pathname: ``,
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
  pathname: PropTypes.string,
};

export default SEO;
